/**
 * Copyright 2024 (C) Blink Tech LLC - All Rights Reserved
 *
 * This source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/react";

import { EnvStoreContext } from "state/EnvironmentsContext/EnvContext";

// The build-time environment is only a fallback; the real one comes from the env store at runtime.
const applyRuntimeEnvironment = (environment?: string) => {
  const client = Sentry.getClient();
  if (!client || !environment) return;

  const options = client.getOptions();
  if (options.environment === environment) return;

  options.environment = environment;
  Sentry.setTag("environment", environment);
};

const setupSentry = async () => {
  const dsn = import.meta.env.SENTRY_DSN;

  const environment =
    EnvStoreContext.getState().environment ||
    import.meta.env.SENTRY_ENVIRONMENT;

  const _withSentry = import.meta.env.WITH_SENTRY;
  const isLocalhost = window.location.hostname === "localhost";
  if (!dsn || !environment || isLocalhost) {
    console.warn("Sentry is not configured");
    return;
  }

  const client = Sentry.init({
    dsn: dsn,
    //   enabled: withSentry === "true",
    environment: environment,
    // Add optional integrations for additional features
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],
    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,
    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,
  });

  EnvStoreContext.subscribe((state) =>
    applyRuntimeEnvironment(state.environment),
  );

  return client;
};

export default setupSentry;
